import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { submitContactForm } from '../services/api';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await submitContactForm(formData);
    if (result.success) {
      toast({
        title: "Message Sent!",
        description: result.message,
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } else {
      toast({
        title: "Error",
        description: result.message,
        variant: "destructive"
      });
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="bg-white min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <h1 className="font-black text-5xl md:text-8xl leading-[0.76] text-[#0A277A] uppercase mb-8">
            Get In Touch
          </h1>
          <p className="text-gray-800 text-xl md:text-2xl max-w-3xl leading-relaxed">
            Have questions about enrollment, partnerships, or donations? We're here to help.
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-16">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-8">
              <div>
                <h2 className="font-black text-3xl text-[#0A277A] uppercase mb-8">
                  Contact Information
                </h2>
              </div>

              <Card className="bg-gray-50 border border-gray-200 p-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-[#3f4816] p-3 rounded-lg">
                    <Mail className="text-[#0A277A]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0A277A] uppercase text-sm mb-2">Email Us</h3>
                    <a href="mailto:info@mosveteran.org" className="text-gray-800 hover:text-[#0A277A] transition-colors">
                      info@mosveteran.org
                    </a>
                  </div>
                </div>
              </Card>

              <Card className="bg-gray-50 border border-gray-200 p-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-[#3f4816] p-3 rounded-lg">
                    <Phone className="text-[#0A277A]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0A277A] uppercase text-sm mb-2">Call Us</h3>
                    <a href="tel:+4915114445479" className="text-gray-800 hover:text-[#0A277A] transition-colors">
                      +49 1511 4445479
                    </a>
                    <p className="text-gray-600 text-sm mt-1">Mon-Fri, 9:00 AM - 5:00 PM CET</p>
                  </div>
                </div>
              </Card>

              <Card className="bg-gray-50 border border-gray-200 p-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-[#3f4816] p-3 rounded-lg">
                    <MapPin className="text-[#0A277A]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0A277A] uppercase text-sm mb-2">Visit Us</h3>
                    <p className="text-gray-800">
                      Kaiserslautern Military Community<br />
                      Germany
                    </p>
                  </div>
                </div>
              </Card>

              <div className="bg-[#3f4816] p-6 rounded-lg">
                <h3 className="font-black text-lg text-[#0A277A] uppercase mb-3">Office Hours</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-gray-800">
                    <span>Monday - Friday</span>
                    <span className="text-gray-600">9:00 AM - 5:00 PM</span>
                  </div>
                  <div className="flex justify-between text-gray-800">
                    <span>Saturday - Sunday</span>
                    <span className="text-gray-600">Closed</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="bg-gray-50 border border-gray-200 p-8">
                <h2 className="font-black text-3xl text-[#0A277A] uppercase mb-6">
                  Send Us a Message
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-[#0A277A] font-semibold uppercase text-sm mb-2">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-white border-gray-200 text-gray-800 focus:border-[#D80000]"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="email" className="text-[#0A277A] font-semibold uppercase text-sm mb-2">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="bg-white border-gray-200 text-gray-800 focus:border-[#D80000]"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-[#0A277A] font-semibold uppercase text-sm mb-2">
                        Phone Number
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        className="bg-white border-gray-200 text-gray-800 focus:border-[#D80000]"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="subject" className="text-[#0A277A] font-semibold uppercase text-sm mb-2">
                      Subject *
                    </Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      placeholder="e.g., Partnership Inquiry, Enrollment Question, Donation"
                      className="bg-white border-gray-200 text-gray-800 focus:border-[#D80000] placeholder:text-gray-600"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-[#0A277A] font-semibold uppercase text-sm mb-2">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      placeholder="Tell us how we can help..."
                      className="bg-white border-gray-200 text-gray-800 focus:border-[#D80000] placeholder:text-gray-600"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#D80000] text-[#1a1c1b] hover:opacity-90 rounded-full py-6 text-lg font-semibold uppercase transition-all hover:scale-105"
                  >
                    <Send size={20} className="mr-2" />
                    Send Message
                  </Button>

                  <p className="text-gray-600 text-sm text-center">
                    We typically respond within 24-48 hours.
                  </p>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="text-center mb-12">
            <h2 className="font-black text-4xl md:text-6xl text-[#0A277A] uppercase mb-4">
              Find Us
            </h2>
            <p className="text-gray-600 text-lg">
              Located in the heart of the Kaiserslautern Military Community
            </p>
          </div>
          <div className="aspect-video rounded-lg overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d82323.29686070024!2d7.6896938!3d49.444722!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4795be2f95571005%3A0x422d4d510db1ba0!2sKaiserslautern%2C%20Germany!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="MOS Academy Location"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Support CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <Card className="bg-[#3f4816] border-none p-12 text-center">
            <h2 className="font-black text-3xl md:text-5xl text-[#0A277A] uppercase mb-4">
              Support Our Mission
            </h2>
            <p className="text-gray-800 text-lg mb-8">
              Help us provide life-changing training to veterans and military families. Every donation makes a difference.
            </p>
            <Button className="bg-[#D80000] text-[#1a1c1b] hover:opacity-90 rounded-full px-10 py-6 text-lg font-semibold uppercase transition-all hover:scale-105">
              Make a Donation
            </Button>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Contact;